<template>
  <div class="guide-ai-dialogue">
    <!-- Simplified Two Column Layout -->
    <div class="dialogue-content">
      <!-- Left Column: Archived Q&A Cards -->
      <div class="archived-cards">
        <div class="cards-header">
          <h3>
            <i class="el-icon-collection"></i>
            Archived Insights ({{ archivedCards.length }})
          </h3>
        </div>
        <div class="cards-container">
          <div v-if="archivedCards.length === 0" class="empty-cards">
            <i class="el-icon-document"></i>
            <p>Your insights will appear here as you explore the topic</p>
          </div>
          <div v-else class="cards-list">
            <div 
              v-for="(card, index) in archivedCards" 
              :key="index"
              class="archived-card"
              @click="viewArchivedCard(card)"
            >
              <div class="card-header">
                <span class="card-number">#{{ index + 1 }}</span>
                <span class="card-timestamp">{{ formatTimestamp(card.timestamp) }}</span>
                <el-button
                  type="danger"
                  size="mini"
                  icon="el-icon-delete"
                  circle
                  @click.stop="deleteCard(card, index)"
                  title="Delete this card"
                ></el-button>
              </div>
              <div class="card-content">
                <div class="card-question">
                  <i class="el-icon-question"></i>
                  <span>{{ truncateText(card.aiQuestion, 60) }}</span>
                </div>
                <div class="card-answer">
                  <i class="el-icon-user"></i>
                  <span>{{ truncateText(card.userAnswer, 60) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Current Dialogue -->
      <div class="dialogue-section">
        <div class="dialogue-header">
          <div>
            <h3>
              <i class="el-icon-chat-dot-round"></i>
              Guide AI - Deep Exploration Mode
            </h3>
            <div v-if="dialogueId" class="dialogue-info">
              <el-tag type="success" size="mini">
                Session ID: {{ dialogueId.substring(0, 8) }}...
              </el-tag>
              <el-tag type="info" size="mini" v-if="dialogueCreatedAt">
                Started: {{ formatTimestamp(dialogueCreatedAt) }}
              </el-tag>
            </div>
          </div>
          <div class="dialogue-actions">
            <el-tag type="info" size="small">
              AI will guide you to explore your topic deeply
            </el-tag>
            <el-button 
              v-if="dialogueId && messages.length > 0" 
              type="text" 
              size="small"
              @click="startNewDialogue"
              icon="el-icon-plus"
            >
              New Dialogue
            </el-button>
          </div>
        </div>

        <div class="messages-container" ref="messagesContainer">
          <!-- Initial system message -->
          <div v-if="messages.length === 0" class="message system">
            <div class="message-content">
              <i class="el-icon-info"></i>
              <p>Welcome to Guide AI! I'm here to help you explore your topic deeply.</p>
              <p>Start by sharing what you'd like to explore, and I'll guide you with thoughtful questions to help you dig deeper and uncover new insights.</p>
            </div>
          </div>

          <!-- Regular messages -->
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message"
            :class="message.role"
          >
            <div class="message-content" v-html="message.content"></div>
          </div>

          <!-- Loading indicator -->
          <div v-if="isLoading" class="message assistant">
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <!-- Input area -->
        <div class="input-area">
          <el-input
            v-model="userInput"
            type="textarea"
            :rows="3"
            :placeholder="getInputPlaceholder()"
            @keyup.enter.native="handleEnter"
          ></el-input>
          <div>
            <Translate :needTranslate="false" @addText="addText" startBtnText="Start recording" endBtnText="Stop recording"></Translate>
            <el-button
              type="primary"
              size="small"
              :disabled="!userInput.trim() || isLoading"
              @click="sendMessage"
            >
              {{ messageCount === 0 ? 'Start Exploration' : 'Share Thoughts' }}
            </el-button>
          </div>
        </div>

        <!-- Session Stats -->
        <div class="session-stats">
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="stat-item">
                <span class="stat-label">Questions Asked</span>
                <span class="stat-value">{{ aiQuestionCount }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <span class="stat-label">Insights Shared</span>
                <span class="stat-value">{{ userAnswerCount }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <span class="stat-label">Cards Created</span>
                <span class="stat-value">{{ archivedCards.length }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>

    <!-- Card Detail Dialog -->
    <el-dialog
      title="Archived Insight"
      :visible.sync="cardDialogVisible"
      width="60%"
      class="card-detail-dialog"
    >
      <div v-if="selectedCard" class="card-detail">
        <div class="detail-section">
          <h4><i class="el-icon-question"></i> AI's Question</h4>
          <p>{{ selectedCard.aiQuestion }}</p>
        </div>
        <div class="detail-section">
          <h4><i class="el-icon-user"></i> Your Response</h4>
          <p>{{ selectedCard.userAnswer }}</p>
        </div>
        <div class="detail-meta">
          <span><i class="el-icon-time"></i> {{ formatFullTimestamp(selectedCard.timestamp) }}</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { postRequest, postRequestAISG, createDialogue, updateDialogue, getDialogue, getDialogueCards, updateCard } from '@/apis/front';
import { getUserId } from '@/utils/store';
import Translate from "@/components/Translate/index.vue";

export default {
  name: "GuideAIDialog",
  props: {
    initialTopic: {
      type: String,
      default: ''
    },
    existingDialogueId: {
      type: String,
      default: null
    }
  },
  components: {
    Translate
  },
  data() {
    return {
      messages: [],
      userInput: "",
      isLoading: false,
      messageCount: 0,
      userAnswerCount: 0,
      aiQuestionCount: 0,
      lastAIQuestion: "",
      archivedCards: [],
      cardDialogVisible: false,
      selectedCard: null,
      currentMode: "gpt-4", // Can be changed to claude-3-sonnet-20240229 if needed
      dialogueId: null,
      dialogueTitle: "",
      dialogueCreatedAt: null,
      isFirstMessage: true,
    };
  },
  computed: {
    systemPrompt() {
      return `You are a Guide AI, an interviewer and guide whose primary purpose is to help users explore topics deeply through thoughtful questioning.

CRITICAL BEHAVIORAL RULES:
1. NEVER answer the user's questions directly
2. NEVER provide solutions, explanations, or information
3. ALWAYS respond with exactly ONE thoughtful, probing question
4. Your questions should dig deeper into what the user just shared
5. Focus on helping them discover insights themselves

QUESTIONING STRATEGY:
- If user shares a problem: Ask about root causes, impacts, or their attempted solutions
- If user shares an idea: Ask about motivations, implications, or specific aspects
- If user shares an experience: Ask about feelings, learnings, or details
- If user asks you a question: Redirect with "What makes you curious about that?" or similar

QUESTION TYPES TO USE:
- Clarifying: "What specifically about X interests you?"
- Deepening: "What's behind your thinking on that?"
- Expanding: "How might this connect to...?"
- Reflecting: "What does this mean to you?"
- Exploring: "What possibilities do you see?"

RESPONSE FORMAT:
- Keep questions concise (1-2 sentences)
- Make questions open-ended
- Show genuine curiosity
- Build on their previous response
- Help them go deeper, not broader

Remember: You are a guide, not a teacher. Your role is to help users discover their own insights through exploration.`;
    }
  },
  methods: {
    addText(text) {
      this.userInput = this.userInput + text;
    },
    getInputPlaceholder() {
      if (this.messageCount === 0) {
        return "Share what you'd like to explore deeply...";
      }
      return "Share your thoughts on the question above...";
    },
    
    handleEnter(e) {
      if (e.shiftKey) return;
      e.preventDefault();
      this.sendMessage();
    },
    
    async sendMessage() {
      if (!this.userInput.trim() || this.isLoading) return;
      
      let userMessage = this.userInput.trim();
      this.userInput = "";
      
      // Create dialogue on first message
      if (this.isFirstMessage && !this.dialogueId) {
        await this.createNewDialogue(userMessage);
        this.isFirstMessage = false;
      }
      
      // Fix grammar and typos before sending
      try {
        const grammarPrompt = `Fix ONLY the spelling mistakes and grammar errors in the following text. Do NOT change the meaning, style, or paraphrase anything. Only correct typos and grammatical errors. Return the corrected text without any additional commentary or explanation.

Text to correct:
${userMessage}`;
        
        this.$message.info('Fixing grammar and typos...');
        
        const grammarResponse = await postRequest('exeModelGeneral', {
          content: grammarPrompt,
          model: 'gpt-4o-mini' // Using a fast model for grammar correction
        });
        
        if (grammarResponse.data.code === 0 && grammarResponse.data.data) {
          const correctedText = grammarResponse.data.data.trim();
          if (correctedText && correctedText !== userMessage) {
            userMessage = correctedText;
            this.$message.success('Grammar and typos fixed');
          }
        }
      } catch (error) {
        console.error('Error fixing grammar:', error);
        // Continue with original message if grammar fixing fails
      }
      
      // Add user message to display
      this.messages.push({
        role: "user",
        content: userMessage
      });
      
      this.messageCount++;
      this.userAnswerCount++;
      
      // Archive Q&A pair if this is the second or later user input
      if (this.messageCount > 1 && this.lastAIQuestion) {
        await this.archiveQAPair(this.lastAIQuestion, userMessage);
      }
      
      // Process with AI
      await this.processWithAI(userMessage);
    },
    
    async processWithAI(userMessage) {
      this.isLoading = true;
      
      try {
        // Prepare messages for AI with system prompt
        const inputMessages = [
          {
            role: "system",
            content: this.systemPrompt
          }
        ];
        
        // Add conversation history
        this.messages.forEach(msg => {
          inputMessages.push({
            role: msg.role,
            content: msg.content
          });
        });
        
        // Add placeholder for assistant response
        this.messages.push({
          role: "assistant",
          content: ""
        });
        
        const currentIndex = this.messages.length - 1;
        let fullResponse = "";
        
        await fetchEventSource(
          "https://web-backend-sg.reachable-edu.com/AIChat/Test.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              model: this.currentMode,
              messages: inputMessages,
            }),
            onopen(response) {
              console.log("Connection established", response);
            },
            onmessage: (event) => {
              // Handle connection close message
              if (event.data === "Connection closed") {
                this.isLoading = false;
                this.lastAIQuestion = fullResponse;
                this.aiQuestionCount++;
                return;
              }
              
              // Handle Claude message_delta
              if (this.currentMode.indexOf("claude") !== -1 && event.data) {
                try {
                  const result = JSON.parse(event.data);
                  if (result.type === "message_delta") {
                    this.isLoading = false;
                    this.lastAIQuestion = fullResponse;
                    this.aiQuestionCount++;
                    return;
                  }
                } catch (e) {
                  // Continue to process other formats
                }
              }
              
              if (event.data) {
                try {
                  const data = JSON.parse(event.data);
                  
                  if (this.currentMode.indexOf("claude") !== -1) {
                    // Claude response handling
                    if (data.type === "content_block_delta" && data.delta?.text) {
                      fullResponse += data.delta.text;
                      this.messages[currentIndex].content = fullResponse;
                    }
                  } else if (data.time && data.content) {
                    // GPT response handling with time and content format
                    fullResponse += data.content;
                    this.messages[currentIndex].content = fullResponse;
                  } else if (data.choices?.[0]?.delta?.content) {
                    // Standard GPT format
                    fullResponse += data.choices[0].delta.content;
                    this.messages[currentIndex].content = fullResponse;
                  }
                  
                  // Scroll to bottom
                  this.$nextTick(() => {
                    this.scrollToBottom();
                  });
                } catch (e) {
                  console.log("Parse error:", e, "Data:", event.data);
                }
              }
              
              if (event.data === "[DONE]") {
                this.isLoading = false;
                this.lastAIQuestion = fullResponse;
                this.aiQuestionCount++;
              }
            },
            onerror: (err) => {
              console.error("Error:", err);
              this.isLoading = false;
              this.$message.error("Failed to get AI response");
            },
            onclose: () => {
              console.log("Connection closed");
              this.isLoading = false;
              // Save the last AI question if we have content
              if (fullResponse) {
                this.lastAIQuestion = fullResponse;
                this.aiQuestionCount++;
              }
            },
          }
        );
      } catch (error) {
        console.error("Error processing AI request:", error);
        this.isLoading = false;
        this.$message.error("Failed to process your message");
      }
    },
    
    async archiveQAPair(aiQuestion, userAnswer) {
      const cardData = {
        title: `Q&A - ${aiQuestion.substring(0, 100)}${aiQuestion.length > 100 ? '...' : ''}`,
        content: `**Question:** ${aiQuestion}\n\n**Answer:** ${userAnswer}`,
        content_type: 'text',  // Ensure content_type is set for SimpleCardView
        aiQuestion: aiQuestion,
        userAnswer: userAnswer,
        timestamp: new Date().toISOString(),
        userId: getUserId(),
        type: 'guide_qa',
        tags: ['guide-ai', 'q&a'],
        editor_type: 'markdown',
        dialogue_id: this.dialogueId,  // Add dialogue_id to card
        metadata: {
          sessionId: this.generateSessionId(),
          dialogueId: this.dialogueId,
          dialogueTitle: this.dialogueTitle
        }
      };
      
      // Save to backend using the correct API
      try {
        const response = await updateCard({
          user_id: getUserId(),
          card_data: cardData,
          is_edit: false
        });
        
        if (response.data.code === 0) {
          // Update local card with backend ID
          cardData.id = response.data.data.id || response.data.data._id?.$oid || response.data.data._id?.$id;
          // Add to local archived cards
          this.archivedCards.push(cardData);
          this.$message.success('Insight archived successfully');
        } else {
          this.$message.error(response.data.msg || 'Failed to save card');
        }
      } catch (error) {
        console.error('Failed to save card:', error);
        this.$message.error('Failed to save card to backend');
      }
    },
    
    generateSessionId() {
      // Generate a unique session ID for this conversation
      if (!this.sessionId) {
        this.sessionId = `guide_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      return this.sessionId;
    },
    
    viewArchivedCard(card) {
      this.selectedCard = card;
      this.cardDialogVisible = true;
    },
    
    truncateText(text, maxLength) {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    
    formatFullTimestamp(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString();
    },
    
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    
    async createNewDialogue(firstMessage) {
      try {
        const dialogueData = {
          user_id: getUserId(),
          title: firstMessage.substring(0, 100) + (firstMessage.length > 100 ? '...' : ''),
          first_message: firstMessage,
          created_at: new Date().toISOString(),
          message_count: 0,
          card_count: 0,
          last_updated: new Date().toISOString()
        };
        
        const response = await createDialogue(dialogueData);
        
        if (response.data.code === 0) {
          this.dialogueId = response.data.data.dialogue_id;
          this.dialogueTitle = dialogueData.title;
          this.dialogueCreatedAt = dialogueData.created_at;
          
          // Emit event for parent component
          this.$emit('dialogue-created', {
            dialogueId: this.dialogueId,
            title: this.dialogueTitle,
            createdAt: this.dialogueCreatedAt
          });
          
          // Update dialogue with conversation history periodically
          this.updateDialogueHistory();
          
          this.$message.success('New dialogue session started');
        } else {
          this.$message.warning('Could not create dialogue session, continuing without tracking');
        }
      } catch (error) {
        console.error('Error creating dialogue:', error);
        this.$message.warning('Could not create dialogue session, continuing without tracking');
      }
    },
    
    async updateDialogueHistory() {
      if (!this.dialogueId) return;
      
      try {
        await updateDialogue({
          dialogue_id: this.dialogueId,
          user_id: getUserId(),
          messages: this.messages,
          message_count: this.messageCount,
          card_count: this.archivedCards.length,
          last_updated: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error updating dialogue history:', error);
      }
    },
    
    startNewDialogue() {
      this.$confirm('Start a new dialogue session? Your current conversation will be saved.', 'New Dialogue', {
        confirmButtonText: 'Start New',
        cancelButtonText: 'Cancel',
        type: 'info'
      }).then(() => {
        // Reset state for new dialogue
        this.messages = [];
        this.userInput = "";
        this.messageCount = 0;
        this.userAnswerCount = 0;
        this.aiQuestionCount = 0;
        this.lastAIQuestion = "";
        this.archivedCards = [];
        this.dialogueId = null;
        this.dialogueTitle = "";
        this.dialogueCreatedAt = null;
        this.isFirstMessage = true;
        
        this.$message.success('Ready to start a new dialogue');
        
        // Emit event for parent component
        this.$emit('new-dialogue-started');
      }).catch(() => {
        // User cancelled
      });
    },
    
    async loadDialogue(dialogueId) {
      try {
        this.isLoading = true;
        
        // Load dialogue metadata
        const dialogueResponse = await getDialogue({
          dialogue_id: dialogueId,
          user_id: getUserId()
        });
        
        if (dialogueResponse.data.code === 0) {
          const dialogue = dialogueResponse.data.data;
          this.dialogueId = dialogue.dialogue_id;
          this.dialogueTitle = dialogue.title;
          this.dialogueCreatedAt = dialogue.created_at;
          this.messages = dialogue.messages || [];
          this.messageCount = dialogue.message_count || this.messages.length;
          this.isFirstMessage = false;
          
          // Load associated cards
          const cardsResponse = await getDialogueCards({
            dialogue_id: dialogueId,
            user_id: getUserId()
          });
          
          if (cardsResponse.data.code === 0) {
            this.archivedCards = cardsResponse.data.data.cards || [];
          }
          
          this.$message.success('Dialogue loaded successfully');
          
          // Scroll to bottom after loading
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        } else {
          this.$message.error('Failed to load dialogue');
        }
      } catch (error) {
        console.error('Error loading dialogue:', error);
        this.$message.error('Failed to load dialogue');
      } finally {
        this.isLoading = false;
      }
    },
    
    async deleteCard(card, index) {
      this.$confirm('Are you sure you want to delete this insight card?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await postRequest('deleteCard', {
            user_id: getUserId(),
            card_id: card.id
          });
          
          if (response.data.code === 0) {
            // Remove from local array
            this.archivedCards.splice(index, 1);
            this.$message.success('Card deleted successfully');
          } else {
            this.$message.error(response.data.msg || 'Failed to delete card');
          }
        } catch (error) {
          console.error('Error deleting card:', error);
          this.$message.error('Failed to delete card');
        }
      }).catch(() => {
        this.$message.info('Delete cancelled');
      });
    }
  },
  
  mounted() {
    // If existing dialogue ID is provided, load it
    if (this.existingDialogueId) {
      this.loadDialogue(this.existingDialogueId);
    } 
    // If initial topic is provided, start the conversation
    else if (this.initialTopic) {
      this.userInput = this.initialTopic;
      this.$nextTick(() => {
        this.sendMessage();
      });
    }
  },
  
  watch: {
    // Watch for dialogue changes
    existingDialogueId(newVal) {
      if (newVal) {
        this.loadDialogue(newVal);
      }
    },
    
    // Update dialogue history when messages change
    messages: {
      deep: true,
      handler() {
        if (this.dialogueId && !this.isLoading) {
          this.updateDialogueHistory();
        }
      }
    }
  }
};
</script>

<style scoped>
.guide-ai-dialogue {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.dialogue-content {
  display: flex;
  gap: 20px;
  height: 100%;
  padding: 20px;
}

/* Left Column - Archived Cards */
.archived-cards {
  width: 35%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.cards-header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}

.cards-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.empty-cards {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-cards i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.archived-card {
  background: #f9fafc;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.archived-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #909399;
  gap: 8px;
}

.card-number {
  font-weight: 600;
  color: #409eff;
}

.card-timestamp {
  flex: 1;
}

.card-header .el-button {
  opacity: 0;
  transition: opacity 0.3s;
  margin-left: auto;
}

.archived-card:hover .card-header .el-button {
  opacity: 1;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-question,
.card-answer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.4;
}

.card-question {
  color: #606266;
}

.card-answer {
  color: #303133;
  padding-left: 16px;
}

.card-question i,
.card-answer i {
  font-size: 14px;
  margin-top: 2px;
  flex-shrink: 0;
}

.card-question i {
  color: #409eff;
}

.card-answer i {
  color: #67c23a;
}

/* Right Column - Dialogue */
.dialogue-section {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.dialogue-header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialogue-info {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.dialogue-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dialogue-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message {
  display: flex;
  align-items: flex-start;
  animation: fadeIn 0.3s;
}

.message.system .message-content {
  background: #f0f9ff;
  border-left: 4px solid #409eff;
  padding: 16px;
  border-radius: 4px;
  color: #606266;
}

.message.system .message-content i {
  color: #409eff;
  margin-right: 8px;
}

.message.user {
  justify-content: flex-end;
}

.message.user .message-content {
  background: #409eff;
  color: white;
  padding: 12px 16px;
  border-radius: 18px 18px 4px 18px;
  max-width: 70%;
  word-wrap: break-word;
}

.message.assistant .message-content {
  background: #f4f4f5;
  color: #303133;
  padding: 12px 16px;
  border-radius: 18px 18px 18px 4px;
  max-width: 70%;
  word-wrap: break-word;
}

.loading-dots {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background: #909399;
  border-radius: 50%;
  animation: loadingDot 1.4s infinite;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes loadingDot {
  0%, 60%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  30% {
    transform: scale(1.3);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.input-area {
  padding: 16px 20px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.input-area .el-textarea {
  flex: 1;
}

/* Session Stats */
.session-stats {
  padding: 12px 20px;
  background: #f9fafc;
  border-top: 1px solid #ebeef5;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.stat-value {
  display: block;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

/* Card Detail Dialog */
.card-detail-dialog .card-detail {
  padding: 20px;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h4 {
  color: #303133;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-section p {
  color: #606266;
  line-height: 1.6;
  margin: 0;
  padding-left: 24px;
}

.detail-meta {
  color: #909399;
  font-size: 12px;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}
</style>